import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { 
  Box, 
  Typography, 
  Grid, 
  Tabs, 
  Tab, 
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Alert
} from "@mui/material";
import { 
  ViewModule, 
  ViewList, 
  FilterList,
  Sort,
  PlayCircleOutline,
  MovieCreation
} from "@mui/icons-material";
import ApiService from "../../services/api-service";
import VideoCard from "../VideoCard/VideoCard";
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import './VideoListEnhanced.css';

export default function VideoListEnhanced({ studioId, isOwner }) {
  const history = useHistory();
  const [recordings, setRecordings] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [sortBy, setSortBy] = useState('newest');
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [sortAnchor, setSortAnchor] = useState(null);

  useEffect(() => {
    fetchData();
  }, [studioId]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      await Promise.all([fetchRecordings(), fetchVideos()]);
      setLoading(false);
    } catch (err) {
      setError("Error loading data. Please try again.");
      setLoading(false);
    }
  };

  const fetchRecordings = async () => {
    try {
      const response = await ApiService.RECORDINGS(`studioId=${studioId}&pageNo=1&pageSize=50`);
      if (response.result?.length > 0) {
        // Format recordings to match video structure for VideoCard
        const formattedRecordings = response.result.map((item) => ({
          _id: item._id,
          title: item.title || `Live Stream ${formatDistanceToNow(new Date(item.createdAt), { addSuffix: true })}`,
          thumbnailUrl: item.thumbnailImg,
          duration: item.duration,
          views: item.views || 0,
          createdAt: item.createdAt,
          uploader: item.uploader || { fullName: 'Studio' },
          url: item.url,
          type: 'recording'
        }));
        setRecordings(formattedRecordings);
      }
    } catch (err) {
      console.error("Error fetching recordings:", err);
    }
  };

  const fetchVideos = async () => {
    try {
      const response = await ApiService.VIDEO_USER(studioId);
      if (response?.result?.length > 0) {
        const formattedVideos = response.result.map((item) => ({
          _id: item._id,
          title: item.title,
          thumbnailUrl: item.thumbnail?.url,
          duration: item.duration,
          views: item.views || 0,
          createdAt: item.createdAt,
          uploader: item.uploader || { fullName: 'Studio' },
          url: item.video?.url,
          type: 'video'
        }));
        setVideos(formattedVideos);
      }
    } catch (err) {
      console.error("Error fetching videos:", err);
    }
  };

  const handleVideoClick = (media) => {
    // Navigate to the full video player page
    history.push(`/app/video-player/${studioId}/${media._id}`);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const getSortedContent = (content) => {
    const sorted = [...content];
    switch (sortBy) {
      case 'newest':
        return sorted.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      case 'oldest':
        return sorted.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      case 'mostViewed':
        return sorted.sort((a, b) => (b.views || 0) - (a.views || 0));
      case 'title':
        return sorted.sort((a, b) => a.title.localeCompare(b.title));
      default:
        return sorted;
    }
  };

  const getCurrentContent = () => {
    if (activeTab === 0) return getSortedContent([...recordings, ...videos]);
    if (activeTab === 1) return getSortedContent(recordings);
    return getSortedContent(videos);
  };

  const getTabLabel = (label, count) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {label}
      <Chip 
        label={count} 
        size="small" 
        sx={{ 
          height: 20,
          backgroundColor: 'rgba(255,255,255,0.1)',
          color: '#fff'
        }} 
      />
    </Box>
  );

  const renderContent = () => {
    const content = getCurrentContent();

    if (loading) {
      return (
        <Grid container spacing={3}>
          {[...Array(6)].map((_, i) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
              <Skeleton variant="rectangular" height={180} sx={{ bgcolor: 'grey.800' }} />
              <Skeleton variant="text" sx={{ bgcolor: 'grey.800', mt: 1 }} />
              <Skeleton variant="text" width="60%" sx={{ bgcolor: 'grey.800' }} />
            </Grid>
          ))}
        </Grid>
      );
    }

    if (content.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <MovieCreation sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No content available yet
          </Typography>
        </Box>
      );
    }

    return (
      <Grid container spacing={3}>
        {content.map((item) => (
          <Grid 
            item 
            xs={12} 
            sm={viewMode === 'list' ? 12 : 6} 
            md={viewMode === 'list' ? 12 : 4} 
            lg={viewMode === 'list' ? 12 : 3} 
            key={item._id}
          >
            <Box sx={{ position: 'relative' }}>
              <VideoCard 
                video={item} 
                onClick={() => handleVideoClick(item)}
                showStats={true}
              />
              {item.type === 'recording' && (
                <Chip
                  icon={<PlayCircleOutline sx={{ fontSize: 16 }} />}
                  label="Live Recording"
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    backgroundColor: 'rgba(255, 0, 0, 0.8)',
                    color: '#fff',
                    zIndex: 1
                  }}
                />
              )}
            </Box>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <Box sx={{ 
      backgroundColor: '#0a0a0a',
      minHeight: '100vh',
      color: '#fff',
      p: 3
    }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 3 }}>
          Studio Content
        </Typography>

        {/* Controls */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          mb: 3
        }}>
          {/* Tabs */}
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            sx={{
              '& .MuiTab-root': {
                color: 'rgba(255,255,255,0.7)',
                textTransform: 'none',
                fontSize: '16px',
                '&.Mui-selected': {
                  color: '#fff'
                }
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#fff'
              }
            }}
          >
            <Tab label={getTabLabel('All', recordings.length + videos.length)} />
            <Tab label={getTabLabel('Live Recordings', recordings.length)} />
            <Tab label={getTabLabel('Videos', videos.length)} />
          </Tabs>

          {/* Right side controls */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            {/* Sort */}
            <IconButton 
              onClick={(e) => setSortAnchor(e.currentTarget)}
              sx={{ color: '#fff' }}
            >
              <Sort />
            </IconButton>
            <Menu
              anchorEl={sortAnchor}
              open={Boolean(sortAnchor)}
              onClose={() => setSortAnchor(null)}
              PaperProps={{
                sx: {
                  backgroundColor: '#1a1a1a',
                  color: '#fff'
                }
              }}
            >
              <MenuItem onClick={() => { setSortBy('newest'); setSortAnchor(null); }}>
                Newest First
              </MenuItem>
              <MenuItem onClick={() => { setSortBy('oldest'); setSortAnchor(null); }}>
                Oldest First
              </MenuItem>
              <MenuItem onClick={() => { setSortBy('mostViewed'); setSortAnchor(null); }}>
                Most Viewed
              </MenuItem>
              <MenuItem onClick={() => { setSortBy('title'); setSortAnchor(null); }}>
                Title (A-Z)
              </MenuItem>
            </Menu>

            {/* View Mode */}
            <IconButton 
              onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
              sx={{ color: '#fff' }}
            >
              {viewMode === 'grid' ? <ViewList /> : <ViewModule />}
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3, backgroundColor: 'rgba(211, 47, 47, 0.1)' }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Content */}
      {renderContent()}
    </Box>
  );
}